import { useQuery } from '@tanstack/react-query'
import { ParentsService } from '@/shared/api/rest/services/parents.service'
import { AuthService } from '@/shared/api/rest/services/auth.service'

type ParentChild = {
    _id: string
    firstName: string
    middleName?: string
    lastName: string
    dateOfBirth: string
    bloodGroup: string
    gender: string
    allergies: string[]
    picture: string
    qrCode: string
    qrCodeData: string
    isCheckedIn: boolean
    lastCheckinCode: string
}

type ParentProfileResponse = {
    _id: string
    children: ParentChild[]
}

/**
 * useMyProfileAsParent - fetch the signed-in parent's children.
 * @returns {data: ParentProfileResponse, loading: boolean}
 */
export function useMyProfileAsParent(): {
    loading: boolean
    data: ParentProfileResponse
} {
    const emptyProfile: ParentProfileResponse = { _id: '', children: [] }

    const { data, isLoading } = useQuery<ParentProfileResponse>({
        queryKey: ['my-children'],
        queryFn: async () => {
            // A signed-in user who isn't a parent yet (e.g. a teacher who just
            // got a child) has no Parent record, so /parents/me and its children
            // 404. That's not an error — degrade each call to empty so the page
            // can show the "add your first child" state. Adding a child then
            // auto-creates the Parent record on the backend.
            const [parent, children] = await Promise.all([
                AuthService.getParentMe().catch(() => null),
                ParentsService.myChildren().catch(() => [])
            ])
            return {
                _id: parent?._id ?? '',
                children: (children ?? []) as ParentChild[]
            }
        }
    })

    return {
        loading: isLoading,
        data: data ?? emptyProfile
    }
}
